import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmModal';
import FotoUpload from '../../components/FotoUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { createAluno } from '../../services/alunoService';
import { extensaoService } from '../../services/extensaoService';
import { fotoService } from '../../services/fotoService';
import { useAuth } from '../../contexts/AuthContext';

const initialState = {
  nome: '',
  cpf: '',
  dataNascimento: '',
  naturalidade: '',
  email: '',
  telefoneCelular: '',
  telefoneResidencial: '',
  telefoneComercial: '',
  escolaridade: '',
  estadoCivil: '',
  filiacaoMae: '',
  filiacaoPai: '',
  identidade: '',
  orgaoExpedidor: '',
  cargo: '',
  status: '',
  extensao: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  pais: '',
  nomeIgrejaCongrega: '',
  nomePastor: '',
  paisIgreja: '',
  telefoneIgreja: '',
  logradouroIgreja: '',
  numeroIgreja: '',
  bairroIgreja: '',
  cidadeIgreja: '',
  estadoIgreja: '',
  cepIgreja: '',
  cepIgrejaEstrangeira: '',
  estrangeira: false,
  docEstrangeiro: '',
  docComprovanteResidencia: false,
  docCpf: false,
  docHistoricoEscolar: false,
  docIdentidade: false,
  docCertidaoCasamentoNascimento: false,
  docFoto: false
};

/**
 * Mesmas regras de `validate`, mas devolvendo um erro por campo — é o que a
 * tela precisa para destacar o input certo em vez de mostrar uma frase solta
 * no topo de um formulário com 36 campos.
 * @returns {Record<string,string>} mapa campo -> mensagem (vazio = tudo certo)
 */
const validarCampos = (form) => {
  const erros = {};
  const nome = (form.nome || '').trim();

  if (!nome) erros.nome = 'O nome é obrigatório.';
  else if (nome.length < 2) erros.nome = 'O nome deve ter pelo menos 2 caracteres.';
  else if (nome.length > 100) erros.nome = 'O nome deve ter até 100 caracteres.';

  if (form.estrangeira) {
    const doc = (form.docEstrangeiro || '').trim();
    if (!doc) erros.docEstrangeiro = 'O documento estrangeiro é obrigatório.';
    else if (doc.length > 50) erros.docEstrangeiro = 'Documento estrangeiro deve ter até 50 caracteres.';
  } else {
    const cpf = (form.cpf || '').trim();
    if (!cpf) erros.cpf = 'O CPF é obrigatório.';
    else if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(cpf)) erros.cpf = 'CPF incompleto — informe os 11 dígitos.';
  }

  if (!form.dataNascimento) {
    erros.dataNascimento = 'Data de nascimento é obrigatória.';
  } else {
    const nascimento = new Date(form.dataNascimento);
    if (nascimento > new Date()) erros.dataNascimento = 'Data de nascimento não pode estar no futuro.';
    else if (nascimento.getFullYear() < 1900) erros.dataNascimento = 'Data de nascimento deve ser posterior a 1900.';
  }

  const naturalidade = (form.naturalidade || '').trim();
  if (!naturalidade) erros.naturalidade = 'Naturalidade é obrigatória.';
  else if (naturalidade.length < 2 || naturalidade.length > 100) {
    erros.naturalidade = 'Naturalidade deve ter entre 2 e 100 caracteres.';
  }

  if (!form.extensao) {
    erros.extensao = 'Extensão é obrigatória — sem extensão não é possível gerar matrícula.';
  }

  if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) erros.email = 'E-mail inválido.';
  if (form.estado && form.estado.length > 2) erros.estado = 'UF deve ter 2 letras (ex.: RJ).';

  return erros;
};

const validate = (form) => {
  if (!form.nome.trim()) return 'O nome é obrigatório.';
  if (form.nome.length > 100) return 'O nome deve ter até 100 caracteres.';
  
  // Validação condicional baseada em ser estrangeiro ou não
  if (form.estrangeira) {
    if (!form.docEstrangeiro || !form.docEstrangeiro.trim()) return 'O documento estrangeiro é obrigatório.';
    if (form.docEstrangeiro.length > 50) return 'Documento estrangeiro deve ter até 50 caracteres.';
  } else {
    if (!form.cpf || !form.cpf.trim()) return 'O CPF é obrigatório.';
    if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(form.cpf)) return 'CPF inválido.';
  }
  
  if (!form.dataNascimento) return 'Data de nascimento é obrigatória.';
  if (!form.naturalidade || !form.naturalidade.trim()) return 'Naturalidade é obrigatória.';
  if (form.naturalidade && (form.naturalidade.length < 2 || form.naturalidade.length > 100)) return 'Naturalidade deve ter entre 2 e 100 caracteres.';
  if (!form.extensao) return 'Extensão é obrigatória - sem extensão não é possível gerar matrícula.';
  if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return 'Email inválido.';
  if (form.telefoneCelular && form.telefoneCelular.length > 15) return 'Telefone celular deve ter até 15 caracteres.';
  if (form.telefoneResidencial && form.telefoneResidencial.length > 15) return 'Telefone residencial deve ter até 15 caracteres.';
  if (form.telefoneComercial && form.telefoneComercial.length > 15) return 'Telefone comercial deve ter até 15 caracteres.';
  if (form.identidade && form.identidade.length > 20) return 'Identidade deve ter até 20 caracteres.';
  if (form.orgaoExpedidor && form.orgaoExpedidor.length > 30) return 'Órgão expedidor deve ter até 30 caracteres.';
  if (form.status && form.status.length > 30) return 'Status deve ter até 30 caracteres.';
  if (form.bairro && form.bairro.length > 50) return 'Bairro deve ter até 50 caracteres.';
  if (form.cidade && form.cidade.length > 100) return 'Cidade deve ter até 100 caracteres.';
  if (form.estado && form.estado.length > 2) return 'Estado deve ter até 2 caracteres.';
  if (form.cep && form.cep.length > 10) return 'CEP deve ter até 10 caracteres.';
  if (form.logradouro && form.logradouro.length > 100) return 'Logradouro deve ter até 100 caracteres.';
  if (form.numero && form.numero.length > 30) return 'Número deve ter até 30 caracteres.';
  if (form.complemento && form.complemento.length > 255) return 'Complemento deve ter até 255 caracteres.';
  if (form.nomeIgrejaCongrega && form.nomeIgrejaCongrega.length > 100) return 'Nome da igreja deve ter até 100 caracteres.';
  if (form.nomePastor && form.nomePastor.length > 100) return 'Nome do pastor deve ter até 100 caracteres.';
  if (form.pais && form.pais.length > 50) return 'País deve ter até 50 caracteres.';
  if (form.paisIgreja && form.paisIgreja.length > 50) return 'País da Igreja deve ter até 50 caracteres.';
  return '';
};

// Funções para aplicar máscaras
const applyMask = {
  cpf: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers
      .slice(0, 11)
      .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      .replace(/(\d{3})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4')
      .replace(/(\d{3})(\d{3})(\d{2})/, '$1.$2.$3')
      .replace(/(\d{3})(\d{2})/, '$1.$2');
  },
  
  telefone: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return `(${numbers}`;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
    } else if (numbers.length <= 10) {
      // Telefone fixo: (21)3106-3491
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
    } else {
      // Celular: (21)98029-8201
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  },
  
  cep: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers
      .slice(0, 8)
      .replace(/(\d{5})(\d{3})/, '$1-$2')
      .replace(/(\d{5})(\d{1,2})/, '$1-$2');
  }
};

const AlunoCreate = () => {
  const { user } = useAuth();
  const nivelAcesso = user?.nivelAcesso || user?.perfil?.nivelAcesso || user?.nivel_acesso || 0;
  
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({}); // Novo estado para erros por campo
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [extensoes, setExtensoes] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [fotoFile, setFotoFile] = useState(null);
  const navigate = useNavigate();

  // Função para obter classes CSS do campo (com erro ou normal)
  const getFieldClass = (fieldName, baseClass = "w-full border rounded px-3 py-2") => {
    return fieldErrors[fieldName] 
      ? `${baseClass} border-red-500 focus:ring-red-400 bg-red-50`
      : `${baseClass} focus:outline-none focus:ring-2 focus:ring-green-400`;
  };

  // Limpar mensagens de erro após 8 segundos APENAS se não houver erros de campo
  useEffect(() => {
    if (error && Object.keys(fieldErrors).length === 0) {
      // Só limpa automaticamente se for erro genérico (sem erros de campo)
      const timer = setTimeout(() => {
        setError('');
      }, 8000);
      return () => clearTimeout(timer);
    }
    // Se houver fieldErrors, NÃO limpar automaticamente - usuário precisa corrigir
  }, [error, fieldErrors]);

  // Limpar mensagens de sucesso após 3 segundos
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Scroll para o topo quando houver mensagens de feedback
  useEffect(() => {
    if (error || success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, success]);

  // Carregar extensões
  useEffect(() => {
    const loadData = async () => {
      setLoadingLists(true);
      try {
        const extensoesResponse = await extensaoService.list({ limit: 1000 });
        setExtensoes(extensoesResponse.data || []);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        setExtensoes([]);
      } finally {
        setLoadingLists(false);
      }
    };
    
    loadData();
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
    setError('');
    // Limpar erro do campo específico quando o usuário editar
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  const handleChangeMasked = e => {
    const { name, value, type, checked } = e.target;
    let formattedValue = value;

    // Aplicar máscaras específicas
    switch (name) {
      case 'cpf':
        formattedValue = applyMask.cpf(value);
        break;
      case 'telefoneCelular':
      case 'telefoneResidencial':
      case 'telefoneComercial':
      case 'telefoneIgreja':
        formattedValue = applyMask.telefone(value);
        break;
      case 'cep':
      case 'cepIgreja':
      case 'cepIgrejaEstrangeira':
        formattedValue = applyMask.cep(value);
        break;
      default:
        formattedValue = value;
    }

    setForm({ ...form, [name]: type === 'checkbox' ? checked : formattedValue });
    setError('');
    // Limpar erro do campo específico quando o usuário editar
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // A validação do cliente checa só o que dá para checar aqui (campos
    // obrigatórios e formato). O backend continua sendo a autoridade — CPF já
    // cadastrado, extensão inexistente etc. seguem vindo dele, por campo.
    // Antes esta checagem estava desligada: o usuário confirmava "Deseja
    // realmente cadastrar este aluno?" com o formulário em branco e só então
    // descobria o que faltava.
    const errosDeCampo = validarCampos(form);
    if (Object.keys(errosDeCampo).length > 0) {
      setFieldErrors(errosDeCampo);
      setError('Por favor, corrija os campos destacados em vermelho.');

      const primeiro = document.querySelector(`[name="${Object.keys(errosDeCampo)[0]}"]`);
      if (primeiro) {
        primeiro.scrollIntoView({ behavior: 'smooth', block: 'center' });
        primeiro.focus();
      }
      return;
    }

    setFieldErrors({});
    setError('');
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(''); // Limpar erros anteriores
    setFieldErrors({}); // Limpar erros de campo anteriores
    try {
      // Preparar dados condicionalmente baseado no campo estrangeira
      const dadosParaEnvio = { ...form };
      
      if (form.estrangeira) {
        // Se é estrangeiro, garantir que CPF seja null
        dadosParaEnvio.cpf = null;
      } else {
        // Se não é estrangeiro, garantir que documento estrangeiro seja null
        dadosParaEnvio.docEstrangeiro = null;
      }
      
      const result = await createAluno(dadosParaEnvio);
      
      if (result.success) {
        // Se tem foto, fazer upload
        if (fotoFile && result.data?.idPessoa) {
          try {
            await fotoService.uploadFoto(result.data.idPessoa, fotoFile);
          } catch (fotoError) {
            console.error('Erro ao fazer upload da foto:', fotoError);
            // Não bloqueamos o sucesso do cadastro por erro na foto
          }
        }
        
        setSuccess(true);
        setSuccessMessage(result.message || `Aluno "${form.nome}" foi cadastrado com sucesso!`);
        setTimeout(() => navigate('/alunos'), 2000);
      } else {
        // Processar erros de validação do backend
        if (result.validationErrors && result.validationErrors.length > 0) {
          const errors = {};
          result.validationErrors.forEach(err => {
            // Se já existe um erro para esse campo, mantém o primeiro
            if (err.path && !errors[err.path]) {
              errors[err.path] = err.msg;
            }
          });
          setFieldErrors(errors);
          // Mensagem amigável indicando que há campos a corrigir
          setError('Por favor, corrija os campos destacados em vermelho.');
          
          // Scroll para o primeiro campo com erro
          setTimeout(() => {
            const firstErrorField = document.querySelector('.border-red-500');
            if (firstErrorField) {
              firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
              firstErrorField.focus();
            }
          }, 100);
        } else {
          setError(result.error || 'Erro ao cadastrar aluno');
        }
      }
    } catch (err) {
      console.error('Erro ao cadastrar aluno:', err);
      
      // Processar erros de validação do backend (vem no response.data)
      const errorData = err.response?.data;
      
      if (errorData?.validationErrors && Array.isArray(errorData.validationErrors) && errorData.validationErrors.length > 0) {
        const errors = {};
        errorData.validationErrors.forEach(validationErr => {
          // Se já existe um erro para esse campo, mantém o primeiro (mais relevante)
          if (validationErr.path && !errors[validationErr.path]) {
            errors[validationErr.path] = validationErr.msg;
          }
        });
        setFieldErrors(errors);
        setError('Por favor, corrija os campos destacados em vermelho.');
        
        // Scroll para o primeiro campo com erro
        setTimeout(() => {
          const firstErrorField = document.querySelector('.border-red-500');
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstErrorField.focus();
          }
        }, 100);
      } else {
        // Erro genérico
        const errorMessage = err.response?.status === 403 
          ? 'Você não tem permissão para cadastrar alunos.'
          : err.response?.status === 409
          ? 'Já existe um aluno cadastrado com este CPF ou documento.'
          : errorData?.message || errorData?.error || 'Erro ao cadastrar aluno. Tente novamente.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Cadastrar Aluno</h2>
      
      {/* Mensagens de Feedback */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <div className="ml-auto">
              <button 
                onClick={() => setError('')}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-green-800 font-medium">{successMessage || 'Aluno cadastrado com sucesso!'}</p>
              <p className="text-green-600 text-sm mt-1">Redirecionando para a lista de alunos...</p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Dados Pessoais e Foto */}
        <div>
          <h3 className="font-bold text-lg mb-4 text-green-700">Dados Pessoais</h3>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Dados Pessoais - 8 colunas */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome *</label>
                  <input 
                    type="text" 
                    name="nome" 
                    value={form.nome} 
                    onChange={handleChange} 
                    maxLength={100}
                    className={getFieldClass('nome')}
                    placeholder="Nome completo" 
                  />
                  {fieldErrors.nome && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.nome}</p>
                  )}
                </div>
                <div className="flex items-center mt-6">
                  <input type="checkbox" name="estrangeira" checked={form.estrangeira} onChange={handleChange} className="mr-2" />
                  <label className="text-sm">Estrangeiro</label>
                </div>
                {!form.estrangeira ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">CPF *</label>
                    <input 
                      type="text" 
                      name="cpf" 
                      value={form.cpf} 
                      onChange={handleChangeMasked} 
                      maxLength={14}
                      className={getFieldClass('cpf')}
                      placeholder="000.000.000-00" 
                    />
                    {fieldErrors.cpf && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.cpf}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-1">Documento Estrangeiro *</label>
                    <input 
                      type="text" 
                      name="docEstrangeiro" 
                      value={form.docEstrangeiro} 
                      onChange={handleChange} 
                      maxLength={50}
                      className={getFieldClass('docEstrangeiro')}
                      placeholder="Documento de identificação" 
                    />
                    {fieldErrors.docEstrangeiro && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.docEstrangeiro}</p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Data de Nascimento *</label>
                  <input 
                    type="date" 
                    name="dataNascimento" 
                    value={form.dataNascimento} 
                    onChange={handleChange}
                    className={getFieldClass('dataNascimento')}
                  />
                  {fieldErrors.dataNascimento && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.dataNascimento}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Naturalidade (Cidade) *</label>
                  <input 
                    type="text" 
                    name="naturalidade" 
                    value={form.naturalidade} 
                    onChange={handleChange} 
                    maxLength={100}
                    className={getFieldClass('naturalidade')}
                    placeholder="Cidade de nascimento" 
                  />
                  {fieldErrors.naturalidade && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.naturalidade}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={form.email} 
                    onChange={handleChange} 
                    maxLength={60} 
                    className={getFieldClass('email')}
                    placeholder="Email" 
                  />
                  {fieldErrors.email && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>
                  )}
                </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Celular</label>
              <input type="text" name="telefoneCelular" value={form.telefoneCelular} onChange={handleChangeMasked} maxLength={15} className={getFieldClass('telefoneCelular')} placeholder="(00)00000-0000" />
              {fieldErrors.telefoneCelular && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.telefoneCelular}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Residencial</label>
              <input type="text" name="telefoneResidencial" value={form.telefoneResidencial} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Comercial</label>
              <input type="text" name="telefoneComercial" value={form.telefoneComercial} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Escolaridade</label>
              <select name="escolaridade" value={form.escolaridade} onChange={handleChange} className={getFieldClass('escolaridade')}>
                <option value="">Selecione a escolaridade</option>
                <option value="Fundamental Incompleto">Fundamental Incompleto</option>
                <option value="Fundamental Completo">Fundamental Completo</option>
                <option value="Ensino Médio Incompleto">Ensino Médio Incompleto</option>
                <option value="Ensino Médio Completo">Ensino Médio Completo</option>
                <option value="Ensino Superior Incompleto">Ensino Superior Incompleto</option>
                <option value="Ensino Superior Completo">Ensino Superior Completo</option>
                <option value="Mestrado">Mestrado</option>
                <option value="Doutorado">Doutorado</option>
              </select>
              {fieldErrors.escolaridade && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.escolaridade}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado Civil</label>
              <select name="estadoCivil" value={form.estadoCivil} onChange={handleChange} className={getFieldClass('estadoCivil')}>
                <option value="">Selecione o estado civil</option>
                <option value="Casado(a)">Casado(a)</option>
                <option value="Solteiro(a)">Solteiro(a)</option>
                <option value="Divorciado(a)">Divorciado(a)</option>
                <option value="Viúvo(a)">Viúvo(a)</option>
                <option value="União Estável">União Estável</option>
              </select>
              {fieldErrors.estadoCivil && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.estadoCivil}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Filiação Mãe</label>
              <input type="text" name="filiacaoMae" value={form.filiacaoMae} onChange={handleChange} maxLength={100} className={getFieldClass('filiacaoMae')} placeholder="Nome da mãe" />
              {fieldErrors.filiacaoMae && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.filiacaoMae}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Filiação Pai</label>
              <input type="text" name="filiacaoPai" value={form.filiacaoPai} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Nome do pai" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Identidade</label>
              <input type="text" name="identidade" value={form.identidade} onChange={handleChange} maxLength={20} className={getFieldClass('identidade')} placeholder="Identidade" />
              {fieldErrors.identidade && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.identidade}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Órgão Expedidor</label>
              <input type="text" name="orgaoExpedidor" value={form.orgaoExpedidor} onChange={handleChange} maxLength={30} className={getFieldClass('orgaoExpedidor')} placeholder="Órgão Expedidor" />
              {fieldErrors.orgaoExpedidor && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.orgaoExpedidor}</p>
              )}
            </div>
              </div>
            </div>
            
            {/* Foto - 4 colunas */}
            <div className="lg:col-span-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 h-full flex flex-col items-center justify-center">
                <FotoUpload
                  currentPhotoUrl={null}
                  onPhotoChange={(file) => setFotoFile(file)}
                  onPhotoRemove={() => setFotoFile(null)}
                  label="Foto do Aluno"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Dados Acadêmicos */}
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Cargo</label>
              <select name="cargo" value={form.cargo} onChange={handleChange} className={getFieldClass('cargo')}>
                <option value="">Selecione o cargo</option>
                <option value="Pastor(a)">Pastor(a)</option>
                <option value="Missionário(a)">Missionário(a)</option>
                <option value="Evangelista">Evangelista</option>
                <option value="Presbítero">Presbítero</option>
                <option value="Diácono(nisa)">Diácono(nisa)</option>
                <option value="Aux. de Trabalho">Aux. de Trabalho</option>
                <option value="Membro">Membro</option>
                <option value="Líder de Dpto">Líder de Dpto</option>
                <option value="Congregado">Congregado</option>
              </select>
              {fieldErrors.cargo && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cargo}</p>
              )}
            </div>
            {nivelAcesso > 3 && (
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select name="status" value={form.status} onChange={handleChange} className="w-full border rounded px-3 py-2">
                  <option value="">Selecione o status</option>
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                  <option value="Concluído">Concluído</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Extensão *</label>
              <select 
                name="extensao" 
                value={form.extensao || ''} 
                onChange={handleChange} 
                className={getFieldClass('extensao')}
                disabled={loadingLists}
              >
                <option value="">
                  {loadingLists ? 'Carregando extensões...' : 'Selecione uma extensão'}
                </option>
                {extensoes.map(extensao => (
                  <option key={extensao.idExtensao} value={extensao.idExtensao}>
                    {extensao.nome}
                  </option>
                ))}
              </select>
              {fieldErrors.extensao && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.extensao}</p>
              )}
            </div>
          </div>
        </div>
        {/* Endereço */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Bairro</label>
              <input type="text" name="bairro" value={form.bairro} onChange={handleChange} maxLength={50} className={getFieldClass('bairro')} placeholder="Bairro" />
              {fieldErrors.bairro && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.bairro}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cidade</label>
              <input type="text" name="cidade" value={form.cidade} onChange={handleChange} maxLength={100} className={getFieldClass('cidade')} placeholder="Cidade" />
              {fieldErrors.cidade && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cidade}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <input type="text" name="estado" value={form.estado} onChange={handleChange} maxLength={2} className={getFieldClass('estado')} placeholder="UF" />
              {fieldErrors.estado && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.estado}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <input type="text" name="pais" value={form.pais} onChange={handleChange} maxLength={50} className={getFieldClass('pais')} placeholder="País" />
              {fieldErrors.pais && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.pais}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CEP</label>
              <input type="text" name="cep" value={form.cep} onChange={handleChangeMasked} maxLength={10} className={getFieldClass('cep')} placeholder="00000-000" />
              {fieldErrors.cep && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cep}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Logradouro</label>
              <input type="text" name="logradouro" value={form.logradouro} onChange={handleChange} maxLength={100} className={getFieldClass('logradouro')} placeholder="Logradouro" />
              {fieldErrors.logradouro && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.logradouro}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número</label>
              <input type="text" name="numero" value={form.numero} onChange={handleChange} maxLength={30} className={getFieldClass('numero')} placeholder="Número" />
              {fieldErrors.numero && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.numero}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Complemento</label>
              <input type="text" name="complemento" value={form.complemento} onChange={handleChange} maxLength={255} className="w-full border rounded px-3 py-2" placeholder="Complemento" />
            </div>
          </div>
        </div>
        {/* Igreja */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Igreja</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome Igreja</label>
              <input type="text" name="nomeIgrejaCongrega" value={form.nomeIgrejaCongrega} onChange={handleChange} maxLength={100} className={getFieldClass('nomeIgrejaCongrega')} placeholder="Nome da Igreja" />
              {fieldErrors.nomeIgrejaCongrega && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.nomeIgrejaCongrega}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nome Pastor</label>
              <input type="text" name="nomePastor" value={form.nomePastor} onChange={handleChange} maxLength={100} className={getFieldClass('nomePastor')} placeholder="Nome do Pastor" />
              {fieldErrors.nomePastor && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.nomePastor}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País da Igreja</label>
              <input type="text" name="paisIgreja" value={form.paisIgreja} onChange={handleChange} maxLength={50} className={getFieldClass('paisIgreja')} placeholder="País da Igreja" />
              {fieldErrors.paisIgreja && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.paisIgreja}</p>
              )}
            </div>
          </div>
        </div>
        {/* Documentos Entregues - Visível apenas para nível de acesso > 3 */}
        {nivelAcesso > 3 && (
          <div>
            <h3 className="font-bold text-lg mb-2 text-green-700">Documentos Entregues</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center">
                <input type="checkbox" name="docComprovanteResidencia" checked={form.docComprovanteResidencia} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Comprovante de Residência</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docHistoricoEscolar" checked={form.docHistoricoEscolar} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Histórico Escolar</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docIdentidade" checked={form.docIdentidade} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Identidade</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docCpf" checked={form.docCpf} onChange={handleChange} className="mr-2" />
                <label className="text-sm">CPF</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docFoto" checked={form.docFoto} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Foto 3x4</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docCertidaoCasamentoNascimento" checked={form.docCertidaoCasamentoNascimento} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Certidão de nascimento/casamento</label>
              </div>
            </div>
          </div>
        )}
        {error && <div className="text-red-600 font-medium mt-2">{error}</div>}
        {success && <div className="text-green-600 font-medium mt-2">Aluno cadastrado com sucesso!</div>}
        <div className="flex gap-4 mt-4 justify-end">
          <button
            type="button"
            className="px-4 py-2 rounded bg-gray-400 text-white font-semibold shadow hover:bg-gray-500 focus:outline-none transition-colors duration-200 flex items-center gap-2"
            onClick={() => navigate('/alunos')}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button
            type="submit"
            className={`px-4 py-2 rounded font-semibold shadow focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-200 flex items-center gap-2 ${
              loading 
                ? 'bg-green-400 cursor-not-allowed' 
                : success 
                  ? 'bg-green-500 text-white' 
                  : 'bg-green-600 text-white hover:bg-green-700'
            }`}
            disabled={loading}
           aria-label="Salvar">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Salvando...</span>
              </>
            ) : success ? (
              <>
                <FontAwesomeIcon icon={faCheck} />
                <span className="hidden sm:inline">Salvo!</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span className="hidden sm:inline">Salvar</span>
              </>
            )}
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        title="Confirmação"
    message="Deseja realmente cadastrar este aluno?"
      />
    </div>
  );
};

export default AlunoCreate;
